import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { YouTubePreview, isValidYouTubeUrl } from '../components/YouTubeEmbed';
import { uploadImage } from '../utils/imageUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreatePost = () => {
  const { token, user, isAdminMode } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editPostId = searchParams.get('edit');
  const isEditing = !!editPostId;
  
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [postData, setPostData] = useState({
    title: '',
    content: '',
    content_type: 'text',
    categories: [],
    image: null,
    image_alt: '',
    image_description: '',
    image_filename: '',
    link_url: '',
    video_url: '',
    podcast_url: '',
    podcast_platform: '',
    podcast_episode_title: '',
    tags: [],
    what_is_it: '',
    boost_level: 'standard',
    admin_boost_size: null,
    is_official: false,
    official_post_type: null,
    is_ad: false,
    article_layout: null,
    excerpt: ''
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [tagInput, setTagInput] = useState('');
  const [showBoostOptions, setShowBoostOptions] = useState(false);
  const [selectedBoostPackage, setSelectedBoostPackage] = useState(null);
  const [linkPreview, setLinkPreview] = useState(null);
  const [linkDetectionTimeout, setLinkDetectionTimeout] = useState(null);
  const [scrapingNews, setScrapingNews] = useState(false);
  const [scrapedArticle, setScrapedArticle] = useState(null);
  const [notification, setNotification] = useState(null);

  // Notification system for sandboxed environment
  const showNotification = (message, type = 'info') => {
    setNotification({ message, type, id: Date.now() });
    setTimeout(() => setNotification(null), 5000); // Auto-hide after 5 seconds
  };

  useEffect(() => {
    fetchCategories();
    if (isEditing) {
      loadPostForEditing();
    }
  }, [isEditing]);

  const loadPostForEditing = async () => {
    try {
      console.log('Loading post for editing, editPostId:', editPostId);
      const headers = { Authorization: `Bearer ${token}` };
      // We need to get the specific post data - let's add this endpoint to backend
      const response = await axios.get(`${API}/posts/${editPostId}`, { headers });
      console.log('Post data loaded successfully:', response.data);
      const post = response.data;
      
      setPostData({
        title: post.title || '',
        content: post.content || '',
        content_type: post.content_type || 'text',
        categories: post.categories || [],
        image: post.image || null,
        link_url: post.link_url || '',
        video_url: post.video_url || '',
        tags: post.tags || [],
        boost_level: post.boost_level || 'standard',
        admin_boost_size: post.admin_boost_size || null,
        is_official: post.is_official || false,
        official_post_type: post.official_post_type || null,
        article_layout: post.article_layout || null,
        excerpt: post.excerpt || ''
      });
      
      if (post.image) {
        setImagePreview(post.image);
      }
      
    } catch (error) {
      console.error('Error loading post for editing:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      // If it's a 401 error, the token is expired - redirect to login
      if (error.response?.status === 401) {
        showNotification('Your session has expired. Please log in again.', 'error');
        navigate('/login');
        return;
      }
      
      showNotification(`Failed to load post for editing: ${error.response?.data?.detail || error.message}`, 'error');
      navigate('/dashboard');
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleInputChange = (e) => {
    setPostData({
      ...postData,
      [e.target.name]: e.target.value
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Immediately show preview of selected file
      const filePreviewUrl = URL.createObjectURL(file);
      setImagePreview(filePreviewUrl);
      
      setUploading(true);
      try {
        // Upload to Cloudinary with boost level optimization
        const result = await uploadImage(file, 'cloudinary', { 
          folder: 'mhb_posts',
          boostLevel: postData.boost_level
        });
        
        setPostData({
          ...postData,
          image: result.url, // Use Cloudinary URL instead of base64
          imagePublicId: result.publicId // Store public ID for potential deletion
        });
        
        // Update preview to use Cloudinary URL
        setImagePreview(result.url);
        
        // Clean up local object URL
        URL.revokeObjectURL(filePreviewUrl);
        
      } catch (error) {
        console.error('Error uploading image:', error);
        showNotification('Failed to upload image. Please try again.', 'error');
        // Clear preview on error
        setImagePreview(null);
        URL.revokeObjectURL(filePreviewUrl);
      } finally {
        setUploading(false);
      }
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !postData.tags.includes(tagInput.trim())) {
      setPostData({
        ...postData,
        tags: [...postData.tags, tagInput.trim()]
      });
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setPostData({
      ...postData,
      tags: postData.tags.filter(tag => tag !== tagToRemove)
    });
  };

  // Facebook-style link detection in content
  const handleContentChange = (e) => {
    const content = e.target.value;
    setPostData({...postData, content});

    // Clear existing timeout
    if (linkDetectionTimeout) {
      clearTimeout(linkDetectionTimeout);
    }

    // Look for URLs in the content
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const urls = content.match(urlRegex);

    if (urls && urls.length > 0) {
      // Set a timeout to avoid too many requests while typing
      const timeout = setTimeout(() => {
        generateLinkPreview(urls[0]); // Use the first URL found
      }, 1500); // Wait 1.5 seconds after user stops typing
      
      setLinkDetectionTimeout(timeout);
    } else {
      // No URLs found, clear preview
      setLinkPreview(null);
    }
  };

  const generateLinkPreview = async (url) => {
    try {
      setLinkPreview({ url, loading: true, domain: new URL(url).hostname.replace('www.', '') });
      
      // Call backend to get rich preview data
      const response = await axios.post(`${API}/posts/link-preview`, { url });
      const previewData = response.data;
      
      if (previewData.error) {
        setLinkPreview({
          url: url,
          domain: new URL(url).hostname.replace('www.', ''),
          title: previewData.headline || 'Link Preview',
          description: previewData.error,
          image: null,
          error: true
        });
      } else {
        setLinkPreview({
          url: url,
          domain: previewData.site_name || new URL(url).hostname.replace('www.', ''),
          title: previewData.headline || 'Untitled',
          description: previewData.content || 'No description available',
          image: previewData.image,
          editable: true // Allow editing
        });
      }
      
      // Store link in postData
      setPostData(prev => ({...prev, link_url: url}));
      
      console.log('🔗 Link preview generated:', previewData.headline || 'No title');
    } catch (error) {
      console.log('Error generating link preview:', error);
      // Fallback to basic preview
      const urlObj = new URL(url);
      const domain = urlObj.hostname.replace('www.', '');
      
      setLinkPreview({
        url: url,
        domain: domain,
        title: 'Link Preview',
        description: 'Click to edit title and description',
        editable: true
      });
      
      setPostData(prev => ({...prev, link_url: url}));
    }
  };

  const scrapeNewsArticle = async (url) => {
    setScrapingNews(true);
    setScrapedArticle(null);
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${API}/posts/scrape-news`, { url }, { headers });
      
      if (response.data) {
        const article = response.data;
        setScrapedArticle(article);
        
        // Auto-fill the form with scraped data
        setPostData(prev => ({
          ...prev,
          title: article.headline || prev.title,
          content: article.content || prev.content,
          image: article.image || prev.image,
          link_url: url
        }));
        
        // Set image preview if we got an image
        if (article.image) {
          setImagePreview(article.image);
        }
        
        console.log('✅ News article scraped successfully:', article.headline);
        console.log('📊 Full article data received:', article);
      }
    } catch (error) {
      console.error('❌ Error scraping news article:', error);
      
      // Show user-friendly error
      if (error.response?.data?.detail) {
        showNotification(`Failed to scrape article: ${error.response.data.detail}`, 'error');
      } else {
        showNotification('Failed to scrape article. Please check the URL and try again.', 'error');
      }
    } finally {
      setScrapingNews(false);
    }
  };

  const handleCategoryChange = (categoryKey) => {
    if (postData.categories.includes(categoryKey)) {
      // Remove category if already selected
      setPostData({
        ...postData,
        categories: postData.categories.filter(cat => cat !== categoryKey)
      });
    } else {
      // Add category if not selected and under limit
      if (postData.categories.length < 5) {
        setPostData({
          ...postData,
          categories: [...postData.categories, categoryKey]
        });
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Debug: Add console logs at the very beginning
    console.log('🚀 handleSubmit called!');
    console.log('🔍 BACKEND_URL:', BACKEND_URL);
    console.log('🔍 API:', API);
    console.log('🔍 Token available:', !!token);
    console.log('🔍 User available:', !!user);

    // Check authentication first
    if (!token || !user) {
      console.log('❌ Authentication check failed - no token or user');
      showNotification('You must be logged in to create posts. Please log in and try again.', 'error');
      setLoading(false);
      return;
    }

    // Check required fields
    if (postData.categories.length === 0) {
      console.log('❌ Categories check failed - no categories selected');
      showNotification('Please select at least one category from the "Fields of Study" section below.', 'warning');
      setLoading(false);
      return;
    }

    console.log('✅ All validation checks passed, preparing submission...');

    try {
      // Prepare submission data with link preview
      const submissionData = { ...postData };
      
      // If there's a link preview, include the title/description
      if (linkPreview && linkPreview.url) {
        submissionData.link_preview = {
          url: linkPreview.url,
          title: linkPreview.title || '',
          description: linkPreview.description || '',
          image: linkPreview.image || null,
          domain: linkPreview.domain || ''
        };
      }
      
      // Debug: Log the data being sent
      console.log('📤 Submitting post data:', submissionData);
      console.log('🔍 Content type being sent:', submissionData.content_type);
      console.log('🔍 Title:', submissionData.title);
      console.log('🔍 Content length:', submissionData.content?.length);
      console.log('🔍 Categories:', submissionData.categories);
      console.log('🔍 Token available:', !!token);
      console.log('🔍 User info:', user?.username);
      
      const headers = { Authorization: `Bearer ${token}` };
      console.log('🔍 Headers:', headers);
      
      if (isEditing) {
        console.log('✏️ Editing existing post...');
        // Update existing post
        const response = await axios.put(`${API}/posts/${editPostId}/edit`, submissionData, { headers });
        console.log('Post update response:', response.data);
        showNotification('🎉 Post updated successfully!', 'success');
      } else {
        console.log('🆕 Creating new post...');
        console.log('🔗 Making POST request to:', `${API}/posts`);
        
        // Create new post
        const response = await axios.post(`${API}/posts`, submissionData, { headers });
        console.log('✅ Post creation response:', response.data);
        
        const createdPost = response.data;
        
        // Check if user selected a boost package
        if (selectedBoostPackage && !isAdminMode) {
          try {
            // Create Stripe checkout session
            const checkoutResponse = await axios.post(`${API}/payments/checkout`, {
              package_id: selectedBoostPackage,
              post_id: createdPost.id,
              origin_url: window.location.origin
            }, { headers });
            
            // Redirect to Stripe checkout
            window.location.href = checkoutResponse.data.session_url;
            return; // Don't continue with normal flow
            
          } catch (paymentError) {
            console.error('Payment error:', paymentError);
            showNotification('Post created successfully, but there was an issue with the payment system. You can boost your post later from the dashboard.', 'warning');
          }
        }
        
        showNotification('🎉 Post shared with community successfully! It should now appear in Dashboard and My Life page.', 'success');
      }
      
      // Force a small delay to let the backend process
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Close the window/modal and navigate to dashboard
      if (window.opener) {
        // If opened as popup, close the window
        window.close();
      } else {
        // Navigate back to dashboard
        navigate('/dashboard');
      }
      
    } catch (error) {
      console.error('❌ Error submitting post:', error);
      console.error('Error details:', error.response?.data);
      const action = isEditing ? 'update' : 'create';
      
      let errorMsg = error.response?.data?.detail || error.message;
      
      // Enhanced error display for debugging
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response headers:', error.response.headers);
        console.error('Response data:', error.response.data);
        
        // Show more specific error messages
        if (error.response.status === 422) {
          errorMsg = `Validation error: ${JSON.stringify(error.response.data.detail || error.response.data)}`;
        } else if (error.response.status === 401) {
          errorMsg = 'Your session has expired. Please log in again.';
        }
      }
      
      showNotification(`Failed to ${action} post: ${errorMsg}. Please check the browser console for more details.`, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100 py-4 sm:py-8">
      {/* Notification Component */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 max-w-md p-4 rounded-lg shadow-lg border ${
          notification.type === 'success' 
            ? 'bg-green-100 border-green-400 text-green-800' 
            : notification.type === 'error'
            ? 'bg-red-100 border-red-400 text-red-800'
            : notification.type === 'warning'
            ? 'bg-yellow-100 border-yellow-400 text-yellow-800'
            : 'bg-blue-100 border-blue-400 text-blue-800'
        }`}>
          <div className="flex items-start space-x-3">
            <span className="text-xl flex-shrink-0">
              {notification.type === 'success' && '✅'}
              {notification.type === 'error' && '❌'}
              {notification.type === 'warning' && '⚠️'}
              {notification.type === 'info' && 'ℹ️'}
            </span>
            <div className="flex-1">
              <p className="font-medium">{notification.message}</p>
            </div>
            <button
              onClick={() => setNotification(null)}
              className="text-gray-500 hover:text-gray-700 text-lg leading-none"
            >
              ×
            </button>
          </div>
        </div>
      )}
      
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl overflow-hidden">
          {/* Header - Mobile Optimized */}
          <div className="bg-gradient-to-r from-emerald-500 to-teal-600 p-4 sm:p-6 text-white">
            <h1 className="text-2xl sm:text-3xl font-bold mb-2">
              {isEditing ? '✏️ Edit Your Post' : '✨ Share Your Consciousness'}
            </h1>
            <p className="text-emerald-100 text-sm sm:text-base">
              {isEditing 
                ? 'Update your post to refine your message and improve the content.'
                : 'Contribute to our collective awakening by sharing your insights, experiences, and wisdom.'
              }
            </p>
          </div>

          {/* Form - Mobile Optimized */}
          <form onSubmit={handleSubmit} className="p-4 sm:p-8">
            <div className="space-y-4 sm:space-y-6">
              {/* Title Field - Now Optional */}
              <div>
                <label className="block text-base sm:text-lg font-medium text-gray-700 mb-2">
                  Title (Optional)
                </label>
                <input
                  type="text"
                  name="title"
                  value={postData.title}
                  onChange={handleInputChange}
                  className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-base sm:text-lg"
                  placeholder="Add a title to your post (optional)"
                />
              </div>

              {/* Content Field with Link Detection */}
              <div>
                <label className="block text-base sm:text-lg font-medium text-gray-700 mb-2">
                  Content *
                </label>
                <div className="mb-2 text-sm text-gray-600 bg-blue-50 p-3 rounded-lg">
                  <p className="font-medium text-blue-800 mb-1">📝 Formatting & Features:</p>
                  <div className="space-y-1 text-xs">
                    <p>• Paste links to auto-generate previews 🔗</p>
                    <p>• Press Enter twice for paragraph spacing</p>
                  </div>
                </div>
                <textarea
                  name="content"
                  value={postData.content}
                  onChange={handleContentChange}
                  required
                  rows="4"
                  className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-base resize-vertical min-h-[100px] max-h-[500px] overflow-y-auto"
                  style={{ 
                    textAlign: 'left',
                    lineHeight: '1.6',
                    whiteSpace: 'pre-wrap'
                  }}
                  placeholder="What's on your mind? Share your thoughts, experiences, or insights...&#10;&#10;💡 Tip: Paste any link and we'll create a preview automatically!"
                  onInput={(e) => {
                    // Auto-expand textarea
                    e.target.style.height = 'auto';
                    e.target.style.height = e.target.scrollHeight + 'px';
                  }}
                />
              </div>

              {/* Enhanced Link Preview (auto-generated & editable) */}
              {linkPreview && (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                  <div className="flex items-start space-x-3">
                    <span className="text-blue-600 text-xl flex-shrink-0">🔗</span>
                    <div className="flex-1 space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-gray-800">Link Preview</h4>
                        <div className="flex space-x-2">
                          {linkPreview.loading && (
                            <span className="text-blue-600 text-sm animate-pulse">Loading...</span>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              setLinkPreview(null);
                              setPostData(prev => ({...prev, link_url: ''}));
                            }}
                            className="text-xs text-gray-500 hover:text-red-600"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                      
                      {/* Preview Card */}
                      <div className="border rounded-lg overflow-hidden bg-white">
                        {linkPreview.image && (
                          <img 
                            src={linkPreview.image} 
                            alt="Link preview" 
                            className="w-full h-32 object-cover"
                          />
                        )}
                        <div className="p-3">
                          <p className="text-xs text-gray-500 mb-1">{linkPreview.domain}</p>
                          
                          {/* Editable Title */}
                          {linkPreview.editable ? (
                            <div className="space-y-2">
                              <input
                                type="text"
                                value={linkPreview.title || ''}
                                onChange={(e) => setLinkPreview(prev => ({...prev, title: e.target.value}))}
                                className="w-full text-sm font-medium text-gray-900 border border-gray-200 rounded px-2 py-1 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Edit title..."
                              />
                              <textarea
                                value={linkPreview.description || ''}
                                onChange={(e) => setLinkPreview(prev => ({...prev, description: e.target.value}))}
                                rows="2"
                                className="w-full text-xs text-gray-600 border border-gray-200 rounded px-2 py-1 resize-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Edit description..."
                              />
                            </div>
                          ) : (
                            <div>
                              <h5 className="text-sm font-medium text-gray-900 mb-1">
                                {linkPreview.title || 'Untitled'}
                              </h5>
                              <p className="text-xs text-gray-600 line-clamp-2">
                                {linkPreview.description || 'No description available'}
                              </p>
                            </div>
                          )}
                          
                          {linkPreview.error && (
                            <div className="mt-2 text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
                              {linkPreview.description}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {linkPreview.editable && (
                        <div className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                          💡 You can edit the title and description above
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Single Photo Upload */}
              <div>
                <label className="block text-base sm:text-lg font-medium text-gray-700 mb-3">
                  📷 Add Photo (Optional)
                </label>
                <div className="relative">
                  <input
                    type="file"
                    id="image-upload"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploading}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-emerald-400 transition-colors cursor-pointer">
                    {uploading ? (
                      <div className="space-y-4">
                        <div className="text-3xl">⏳</div>
                        <p className="text-emerald-600 font-medium">Uploading image...</p>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-emerald-600 h-2 rounded-full animate-pulse w-3/4"></div>
                        </div>
                      </div>
                    ) : imagePreview ? (
                      <div className="space-y-4">
                        <img 
                          src={imagePreview} 
                          alt="Preview" 
                          className="w-full max-w-md h-32 mx-auto rounded-lg object-cover border shadow-sm"
                        />
                        <div className="text-sm text-green-600 bg-green-50 px-3 py-1 rounded">
                          ✅ Image uploaded successfully
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setImagePreview(null);
                            setPostData({...postData, image: null});
                            const fileInput = document.getElementById('image-upload');
                            if (fileInput) fileInput.value = '';
                          }}
                          className="text-red-600 hover:text-red-800 text-sm underline bg-red-50 px-3 py-1 rounded hover:bg-red-100 transition-colors"
                        >
                          🗑️ Remove Image
                        </button>
                      </div>
                    ) : (
                      <div>
                        <div className="text-3xl mb-2">📸</div>
                        <p className="text-gray-600 text-base">Click or drag to upload an image</p>
                        <p className="text-sm text-gray-500 mt-1">JPG, PNG, GIF up to 10MB</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Video URL (if needed) */}
              {postData.video_url && (
                <div>
                  <label className="block text-base sm:text-lg font-medium text-gray-700 mb-2">
                    YouTube Video URL *
                  </label>
                  <input
                    type="url"
                    name="video_url"
                    value={postData.video_url}
                    onChange={handleInputChange}
                    className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-base"
                    placeholder="https://www.youtube.com/watch?v=..."
                  />
                  <button
                    type="button"
                    onClick={() => setPostData({...postData, video_url: ''})}
                    className="mt-2 text-red-600 hover:text-red-800 text-sm underline"
                  >
                    Remove video
                  </button>
                </div>
              )}

              {/* Podcast Player - Admin Only */}
              {postData.content_type === 'podcast' && isAdminMode && (
                <div>
                  <label className="block text-base sm:text-lg font-medium text-gray-700 mb-2">
                    Podcast Embed URL *
                  </label>
                  <input
                    type="url"
                    name="podcast_url"
                    value={postData.podcast_url}
                    onChange={(e) => {
                      const url = e.target.value;
                      let convertedUrl = url;
                      
                      // Auto-convert Spotify URLs
                      if (url.includes('open.spotify.com/episode/')) {
                        convertedUrl = url.replace('open.spotify.com', 'open.spotify.com/embed');
                      }
                      
                      handleInputChange({
                        target: {
                          name: 'podcast_url',
                          value: convertedUrl
                        }
                      });
                    }}
                    required={postData.content_type === 'podcast'}
                    className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-base"
                    placeholder="https://open.spotify.com/episode/... (will auto-convert to embed)"
                  />
                  
                  {/* Platform Selection */}
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Platform
                    </label>
                    <select
                      name="podcast_platform"
                      value={postData.podcast_platform}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                    >
                      <option value="">Select Platform</option>
                      <option value="spotify">Spotify</option>
                      <option value="apple">Apple Podcasts</option>
                      <option value="google">Google Podcasts</option>
                      <option value="anchor">Anchor</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  {/* Episode Title */}
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Episode Title (optional)
                    </label>
                    <input
                      type="text"
                      name="podcast_episode_title"
                      value={postData.podcast_episode_title}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                      placeholder="Leave blank to use post title"
                    />
                  </div>

                  <div className="mt-2 text-sm text-gray-600">
                    <p>🎧 How to get embed URLs:</p>
                    <ul className="mt-1 space-y-1 text-xs">
                      <li>• <strong>Spotify:</strong> Paste any episode URL - it will auto-convert!</li>
                      <li>• <strong>Apple Podcasts:</strong> Get embed code from the episode page</li>
                      <li>• <strong>Google Podcasts:</strong> Use the share embed link</li>
                      <li>• <strong>Other:</strong> Look for "Embed" or "Share" options</li>
                    </ul>
                    
                    {postData.podcast_url && !postData.podcast_url.includes('/embed/') && postData.podcast_url.includes('spotify.com') && (
                      <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
                        <p className="text-yellow-700 text-xs">⚠️ Regular Spotify URLs won't work. Use: Share → Copy Embed Code</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Podcast Preview */}
                  {postData.podcast_url && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
                      <p className="text-sm font-medium text-gray-700 mb-2">Preview:</p>
                      {postData.podcast_url.includes('/embed/') ? (
                        <iframe
                          src={postData.podcast_url}
                          width="100%"
                          height="152"
                          frameBorder="0"
                          allowtransparency="true"
                          allow="encrypted-media"
                          className="rounded"
                        ></iframe>
                      ) : (
                        <div className="p-4 bg-red-50 border border-red-200 rounded">
                          <p className="text-red-700 text-sm">❌ This URL won't embed properly</p>
                          <p className="text-red-600 text-xs mt-1">
                            For Spotify: Go to episode → Share → Copy Embed Code<br/>
                            For others: Use their embed/iframe URLs
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Add Video Button */}
              {!postData.video_url && (
                <div>
                  <button
                    type="button"
                    onClick={() => setPostData({...postData, video_url: 'https://www.youtube.com/watch?v='})}
                    className="flex items-center space-x-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-emerald-400 transition-colors text-gray-600 hover:text-emerald-600"
                  >
                    <span className="text-xl">🎥</span>
                    <span className="font-medium">Add YouTube Video</span>
                  </button>
                </div>
              )}

              {/* Add Podcast Button - Admin Only */}
              {isAdminMode && postData.content_type !== 'podcast' && (
                <div>
                  <button
                    type="button"
                    onClick={() => setPostData({...postData, content_type: 'podcast', podcast_url: ''})}
                    className="flex items-center space-x-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-400 transition-colors text-gray-600 hover:text-purple-600"
                  >
                    <span className="text-xl">🎧</span>
                    <span className="font-medium">Add Podcast Episode</span>
                  </button>
                </div>
              )}

              {/* Link URL - Mobile Optimized */}
              {postData.content_type === 'link' && (
                <div>
                  <label className="block text-base sm:text-lg font-medium text-gray-700 mb-2">
                    Link URL *
                  </label>
                  <input
                    type="url"
                    name="link_url"
                    value={postData.link_url}
                    onChange={handleInputChange}
                    required={postData.content_type === 'link'}
                    className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-base"
                    placeholder="https://example.com"
                  />
                </div>
              )}

              {/* YouTube Video URL - Mobile Optimized */}
              {postData.content_type === 'video' && (
                <div>
                  <label className="block text-base sm:text-lg font-medium text-gray-700 mb-2">
                    YouTube Video URL *
                  </label>
                  <input
                    type="url"
                    name="video_url"
                    value={postData.video_url}
                    onChange={handleInputChange}
                    required={postData.content_type === 'video'}
                    className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-base"
                    placeholder="https://www.youtube.com/watch?v=..."
                  />
                  <div className="mt-2 text-sm text-gray-600">
                    <p>📺 Supported formats:</p>
                    <ul className="mt-1 space-y-1 text-xs">
                      <li>• https://www.youtube.com/watch?v=VIDEO_ID</li>
                      <li>• https://youtu.be/VIDEO_ID</li>
                      <li>• https://www.youtube.com/embed/VIDEO_ID</li>
                    </ul>
                  </div>
                  
                  {/* YouTube Preview */}
                  {postData.video_url && isValidYouTubeUrl(postData.video_url) && (
                    <div className="mt-4">
                      <YouTubePreview videoUrl={postData.video_url} />
                    </div>
                  )}
                  
                  {postData.video_url && !isValidYouTubeUrl(postData.video_url) && (
                    <div className="mt-3 p-3 bg-red-50 rounded-lg border border-red-200">
                      <p className="text-sm text-red-700 font-medium">❌ Invalid YouTube URL</p>
                      <p className="text-xs text-red-600 mt-1">Please enter a valid YouTube video link</p>
                    </div>
                  )}
                </div>
              )}

              {/* News Article URL - Highlighted */}
              {postData.content_type === 'news' && (
                <div className="relative">
                  <label className="block text-base sm:text-lg font-medium text-gray-700 mb-2">
                    📰 News Article URL *
                  </label>
                  <div className="relative">
                    <input
                      type="url"
                      name="link_url"
                      value={postData.link_url}
                      onChange={handleInputChange}
                      required={postData.content_type === 'news'}
                      className="w-full px-3 sm:px-4 py-3 border-2 border-emerald-400 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-base bg-emerald-50 shadow-md"
                      placeholder="https://news-website.com/article-url"
                      autoFocus={postData.content_type === 'news'}
                      disabled={scrapingNews}
                    />
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      {scrapingNews ? (
                        <div className="animate-spin text-emerald-600 text-lg">⏳</div>
                      ) : (
                        <span className="text-emerald-600 text-lg">🔗</span>
                      )}
                    </div>
                  </div>
                  <div className="mt-2 text-sm text-emerald-700 bg-emerald-50 p-3 rounded-lg border border-emerald-200">
                    <p className="font-medium mb-1">📰 News Post Features:</p>
                    <ul className="space-y-1 text-xs">
                      <li>• Enter the article URL for reference</li>
                      <li>• Copy/paste headline and article content below</li>
                      <li>• Readers get "Open article" button to view source</li>
                      <li>• URLs are validated against harmful sites</li>
                    </ul>
                  </div>
                  
                  {/* Scraped Article Preview */}
                  {scrapedArticle && (
                    <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-start space-x-3">
                        <span className="text-green-600 text-xl">✅</span>
                        <div className="flex-1">
                          <p className="font-medium text-green-800 mb-2">URL Validated Successfully!</p>
                          <div className="space-y-2 text-sm">
                            {scrapedArticle.site_name && (
                              <div>
                                <span className="font-medium">Source:</span> {scrapedArticle.site_name}
                              </div>
                            )}
                          </div>
                          <p className="text-xs text-green-600 mt-2">
                            ✨ Please enter the headline and content manually below
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Categories */}
              <div>
                <label className="block text-lg font-medium text-gray-700 mb-2">
                  Fields of Study *
                </label>
                <p className="text-sm text-gray-600 mb-4">
                  You may post in up to 5 categories
                </p>
                <div className="max-h-60 overflow-y-auto border border-gray-300 rounded-lg p-4 space-y-2">
                  {categories.map((category) => (
                    <label key={category.key} className="flex items-center space-x-3 cursor-pointer hover:bg-gray-50 p-2 rounded">
                      <input
                        type="checkbox"
                        checked={postData.categories.includes(category.key)}
                        onChange={() => handleCategoryChange(category.key)}
                        disabled={!postData.categories.includes(category.key) && postData.categories.length >= 5}
                        className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                      />
                      <span className={`text-sm ${
                        postData.categories.includes(category.key) 
                          ? 'text-emerald-700 font-medium' 
                          : !postData.categories.includes(category.key) && postData.categories.length >= 5
                          ? 'text-gray-400'
                          : 'text-gray-700'
                      }`}>
                        {category.label}
                      </span>
                    </label>
                  ))}
                </div>
                {postData.categories.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm text-gray-600 mb-2">Selected categories ({postData.categories.length}/5):</p>
                    <div className="flex flex-wrap gap-2">
                      {postData.categories.map((catKey) => {
                        const category = categories.find(c => c.key === catKey);
                        return (
                          <span key={catKey} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-emerald-100 text-emerald-800">
                            {category?.label}
                            <button
                              type="button"
                              onClick={() => handleCategoryChange(catKey)}
                              className="ml-2 hover:text-emerald-600"
                            >
                              ×
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* What is it - Image Description for AI/Moderation */}
              {postData.content_type === 'image' && (
                <div>
                  <label className="block text-lg font-medium text-gray-700 mb-2">
                    What is it? <span className="text-sm text-gray-500">(Help us describe your image)</span>
                  </label>
                  <input
                    type="text"
                    value={postData.what_is_it}
                    onChange={(e) => setPostData({ ...postData, what_is_it: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="Briefly describe what's in the image..."
                    maxLength={100}
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    This helps with accessibility and content organization
                  </p>
                </div>
              )}

              {/* Tags */}
              <div>
                <label className="block text-lg font-medium text-gray-700 mb-2">
                  Tags
                </label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {postData.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm flex items-center space-x-1"
                    >
                      <span>#{tag}</span>
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="text-emerald-500 hover:text-emerald-700"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="Add a tag..."
                  />
                  <button
                    type="button"
                    onClick={addTag}
                    className="px-4 py-2 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 transition-colors"
                  >
                    Add
                  </button>
                </div>
              </div>

              {/* Boost Options / Admin Controls */}
              <div className="border-t pt-6">
                {isAdminMode ? (
                  // Admin Content Master Controls
                  <div className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-200">
                    <div className="flex items-center space-x-2 mb-4">
                      <span className="text-2xl">👑</span>
                      <h4 className="font-bold text-purple-800">Content Master Controls</h4>
                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">AUTO VIP BOOST</span>
                    </div>
                    
                    {/* Official Post Toggle */}
                    <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border border-blue-200">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h5 className="font-semibold text-blue-800">🏢 Official Modern Human Being Post</h5>
                          <p className="text-sm text-blue-600">Post as the platform instead of your personal account</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={postData.is_official}
                            onChange={(e) => setPostData({...postData, is_official: e.target.checked})}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>
                      
                      {postData.is_official && (
                        <div className="space-y-4">
                          {/* Official Post Type */}
                          <div>
                            <label className="block text-sm font-medium text-blue-700 mb-2">Official Post Type</label>
                            <select
                              value={postData.official_post_type || ''}
                              onChange={(e) => setPostData({...postData, official_post_type: e.target.value})}
                              className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                              <option value="">Select type...</option>
                              <option value="announcement">📢 Platform Announcement</option>
                              <option value="article">📖 Educational Article</option>
                              <option value="guide">📋 Community Guide</option>
                              <option value="update">🔄 Feature Update</option>
                            </select>
                          </div>
                          
                          {/* Article Layout */}
                          <div>
                            <label className="block text-sm font-medium text-blue-700 mb-2">Article Layout</label>
                            <div className="grid grid-cols-2 gap-2">
                              {[
                                { value: 'standard', label: 'Standard', desc: 'Regular layout' },
                                { value: 'feature', label: 'Feature', desc: 'Highlighted' },
                                { value: 'hero', label: 'Hero', desc: 'Large banner' },
                                { value: 'magazine', label: 'Magazine', desc: 'Editorial style' }
                              ].map((layout) => (
                                <button
                                  key={layout.value}
                                  type="button"
                                  onClick={() => setPostData({...postData, article_layout: layout.value})}
                                  className={`p-2 rounded-lg border transition-colors text-center ${
                                    postData.article_layout === layout.value
                                      ? 'border-blue-500 bg-blue-100 text-blue-800'
                                      : 'border-gray-200 hover:border-blue-300'
                                  }`}
                                >
                                  <div className="font-medium text-sm">{layout.label}</div>
                                  <div className="text-xs text-gray-600">{layout.desc}</div>
                                </button>
                              ))}
                            </div>
                          </div>
                          
                          {/* Post Excerpt */}
                          <div>
                            <label className="block text-sm font-medium text-blue-700 mb-2">Post Excerpt</label>
                            <textarea
                              value={postData.excerpt}
                              onChange={(e) => setPostData({...postData, excerpt: e.target.value})}
                              rows={2}
                              className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder="Brief description that appears in feed previews..."
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* AD Post Option - Admin Only */}
                    {isAdminMode && (
                      <div className="p-4 border-2 border-orange-200 rounded-lg bg-orange-50">
                        <div className="flex items-center justify-between">
                          <div>
                            <h5 className="font-semibold text-orange-800">📢 Advertisement Post</h5>
                            <p className="text-sm text-orange-600">Mark this post as an advertisement</p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={postData.is_ad}
                              onChange={(e) => setPostData({...postData, is_ad: e.target.checked})}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                          </label>
                        </div>
                      </div>
                    )}
                    
                    <div className="space-y-3">
                      <div className="text-sm text-purple-700 mb-4">
                        {postData.is_official ? (
                          <>🏢 <strong>Official Post:</strong> Will appear as "Modern Human Being" with enhanced layouts</>
                        ) : (
                          <>🚀 Your posts automatically get VIP boost and appear at the top of all feeds. Choose your preferred size:</>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <button
                          type="button"
                          onClick={() => setPostData({...postData, admin_boost_size: 'standard'})}
                          className={`p-3 rounded-lg border-2 transition-colors text-center ${
                            postData.admin_boost_size === 'standard'
                              ? 'border-purple-500 bg-purple-100 text-purple-800'
                              : 'border-gray-200 hover:border-purple-300'
                          }`}
                        >
                          <div className="font-medium">Standard</div>
                          <div className="text-xs text-gray-600">1x size</div>
                        </button>
                        
                        <button
                          type="button"
                          onClick={() => setPostData({...postData, admin_boost_size: 'large'})}
                          className={`p-3 rounded-lg border-2 transition-colors text-center ${
                            postData.admin_boost_size === 'large'
                              ? 'border-purple-500 bg-purple-100 text-purple-800'
                              : 'border-gray-200 hover:border-purple-300'
                          }`}
                        >
                          <div className="font-medium">Large</div>
                          <div className="text-xs text-gray-600">2x size</div>
                        </button>
                        
                        <button
                          type="button"
                          onClick={() => setPostData({...postData, admin_boost_size: 'xl'})}
                          className={`p-3 rounded-lg border-2 transition-colors text-center ${
                            postData.admin_boost_size === 'xl'
                              ? 'border-purple-500 bg-purple-100 text-purple-800'
                              : 'border-gray-200 hover:border-purple-300'
                          }`}
                        >
                          <div className="font-medium">XL</div>
                          <div className="text-xs text-gray-600">3x size</div>
                        </button>
                        
                        <button
                          type="button"
                          onClick={() => setPostData({...postData, admin_boost_size: 'featured'})}
                          className={`p-3 rounded-lg border-2 transition-colors text-center ${
                            postData.admin_boost_size === 'featured'
                              ? 'border-purple-500 bg-purple-100 text-purple-800'
                              : 'border-gray-200 hover:border-purple-300'
                          }`}
                        >
                          <div className="font-medium">Featured</div>
                          <div className="text-xs text-gray-600">Full width</div>
                        </button>
                      </div>
                      
                      <div className="mt-4 p-3 bg-purple-100 rounded-lg">
                        <div className="text-sm text-purple-800">
                          ✨ <strong>Your privileges:</strong> Auto VIP boost • Top feed placement • No fees • Custom sizing • Featured status
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  // Regular user boost options
                  <>
                    <button
                      type="button"
                      onClick={() => setShowBoostOptions(!showBoostOptions)}
                      className="flex items-center space-x-2 text-emerald-600 hover:text-emerald-800 font-medium"
                    >
                      <span>🚀</span>
                      <span>Boost This Post (Network Feature)</span>
                      <svg className={`w-4 h-4 transition-transform ${showBoostOptions ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    
                    {showBoostOptions && (
                      <div className="mt-4 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg border border-emerald-200">
                        <h4 className="font-semibold text-emerald-800 mb-3">🌟 Boost Your Post to 3-Column Size</h4>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                            <div>
                              <div className="font-medium">Standard Boost</div>
                              <div className="text-sm text-gray-600">Larger display in main feed for 24 hours</div>
                            </div>
                            <div className="text-right">
                              <div className="font-bold text-emerald-600">$2.99</div>
                              <button 
                                type="button"
                                onClick={() => setSelectedBoostPackage('standard')}
                                className={`text-xs px-3 py-1 border rounded-full transition-colors ${
                                  selectedBoostPackage === 'standard'
                                    ? 'bg-emerald-600 text-white border-emerald-600'
                                    : 'text-emerald-600 hover:text-emerald-800 border-emerald-300 hover:bg-emerald-50'
                                }`}
                              >
                                {selectedBoostPackage === 'standard' ? '✓ Selected' : 'Select'}
                              </button>
                            </div>
                          </div>
                          <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                            <div>
                              <div className="font-medium">Premium Boost</div>
                              <div className="text-sm text-gray-600">Featured placement + 3-column size for 48 hours</div>
                            </div>
                            <div className="text-right">
                              <div className="font-bold text-emerald-600">$7.99</div>
                              <button 
                                type="button"
                                onClick={() => setSelectedBoostPackage('premium')}
                                className={`text-xs px-3 py-1 border rounded-full transition-colors ${
                                  selectedBoostPackage === 'premium'
                                    ? 'bg-emerald-600 text-white border-emerald-600'
                                    : 'text-emerald-600 hover:text-emerald-800 border-emerald-300 hover:bg-emerald-50'
                                }`}
                              >
                                {selectedBoostPackage === 'premium' ? '✓ Selected' : 'Select'}
                              </button>
                            </div>
                          </div>
                          <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-yellow-300 bg-yellow-50">
                            <div>
                              <div className="font-medium text-yellow-800">🏆 VIP Boost</div>
                              <div className="text-sm text-yellow-700">Top placement + 3-column + pin for 72 hours</div>
                            </div>
                            <div className="text-right">
                              <div className="font-bold text-yellow-600">$19.99</div>
                              <button 
                                type="button"
                                onClick={() => setSelectedBoostPackage('vip')}
                                className={`text-xs px-3 py-1 border rounded-full transition-colors ${
                                  selectedBoostPackage === 'vip'
                                    ? 'bg-yellow-600 text-white border-yellow-600'
                                    : 'text-yellow-600 hover:text-yellow-800 border-yellow-400 hover:bg-yellow-100'
                                }`}
                              >
                                {selectedBoostPackage === 'vip' ? '✓ Selected' : 'Select'}
                              </button>
                            </div>
                          </div>
                          {selectedBoostPackage && (
                            <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                              <p className="text-sm text-blue-800">
                                ✨ <strong>{selectedBoostPackage.charAt(0).toUpperCase() + selectedBoostPackage.slice(1)} Boost selected!</strong> 
                                After creating your post, you'll be redirected to secure payment processing.
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Submit Buttons */}
              <div className="flex space-x-4 pt-6">
                <button
                  type="button"
                  onClick={() => navigate('/dashboard')}
                  className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg font-medium hover:shadow-lg transition-shadow disabled:opacity-50"
                >
                  {loading ? 'Publishing...' : '🌟 Share with Community'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreatePost;